import { Component } from 'react';
import type {
    ComponentProps,
    ErrorInfo,
    PropsWithChildren,
    ReactElement,
    ReactNode,
} from 'react';

import { EmptyState } from '@bestseller-bit/frontend-community.components.empty-state';

interface ErrorBoundaryProps extends PropsWithChildren {
    /** The fallback UI to render when an error occurs */
    fallback: ReactElement;
    /** A key used to reset the state. If the key is changed, the error boundary will reset. */
    resetKey?: number | string;
    /** Optional callback when an error is caught */
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
    /** Indicates whether an error has been caught, used for rendering fallback UI */
    hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    public constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    // Update state so the next render will show the fallback UI.
    public static getDerivedStateFromError(_: Error): ErrorBoundaryState {
        // If any errors should be ignored, we can add logic here to check the error message or type
        return { hasError: true };
    }

    // If this component caught an error, we log it and call the onError prop if provided.
    // We intentionally use console.error instead of datadogRum.addError/addReactError:
    // - Datadog RUM already auto-captures console.error, so this still reaches Datadog.
    // - It also keeps the error visible in the browser console for devs previewing the UI.
    // - Calling addError/addReactError as well would double-report the same crash
    //   (once from the console source, once from a custom source).
    // - addReactError would additionally require the @datadog/browser-rum-react package
    //   and reactPlugin for what would only be a cosmetic improvement.
    public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error(
            'Error caught by MicrofrontendErrorBoundary:',
            error,
            errorInfo
        );
        this.props.onError?.(error, errorInfo);
    }

    // Reset error state when resetKey changes (e.g., on route navigation)
    public componentDidUpdate(prevProps: ErrorBoundaryProps): void {
        if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
            this.setState({ hasError: false });
        }
    }

    // The render function decides what to display
    public render(): ReactNode {
        // If there is an error, render the fallback UI
        if (this.state.hasError) {
            return this.props.fallback;
        }

        // If there is no error, render the children
        return this.props.children;
    }
}

interface MicrofrontendErrorBoundaryProps
    extends Omit<Partial<ErrorBoundaryProps>, 'children' | 'fallback'> {
    /** Props that are provided to the inner empty state component. */
    emptyStateProps?: Partial<ComponentProps<typeof EmptyState>>;
}

const MicrofrontendErrorBoundary = ({
    children,
    emptyStateProps,
    ...errorBoundaryProps
}: PropsWithChildren<MicrofrontendErrorBoundaryProps>): ReactElement => (
    <ErrorBoundary
        fallback={
            <EmptyState
                state="failed"
                title="Oops, something went wrong!"
                description="An unexpected error occurred. Please try again."
                {...emptyStateProps}
            >
                {emptyStateProps?.children}
            </EmptyState>
        }
        {...errorBoundaryProps}
    >
        {children}
    </ErrorBoundary>
);

export { MicrofrontendErrorBoundary };
